import { setYaxisRangeBasedOnTracesVisibility } from 'helpers/plotly/yAxisRangeCalculator'
import moment from 'moment-timezone'
import { color } from 'styles/colors'
import _ from 'underscore'

const LEFT_YAXIS = 'y'
const RIGHT_YAXIS = 'y2'

interface Line {
  color: string;
  dash: Plotly.Dash;
  width: number | undefined;
}
interface AwairItems {
  temperature: Line,
  humidity: Line,
  co2: Line,
  voc: Line,
  pm25: Line,
  actualAirflow: Line
}
const awairPlot: AwairItems = {
  temperature: {
    color: color('awairTemp'),
    dash: 'solid',
    width: 2
  },
  humidity: {
    color: color('awairHumidity'),
    dash: 'solid',
    width: 2
  },
  co2: {
    color: color('awairCo2'),
    dash: 'solid',
    width: 2
  },
  voc: {

    color: color('awairVoc'),
    dash: 'solid',
    width: 2
  },
  pm25: {
    color: color('awairPm25'),
    dash: 'solid',
    width: 2
  },
  actualAirflow: {
    color: color('indoorUnitActualAirflow'),
    dash: 'longdashdot',
    width: 2
  }
}

const baseLayout: Partial<Plotly.Layout> = {
  font: {
    family: 'Inter, sans-serif',
    size: 11,
    color: color('rthChartAxisLabel')
  },
  showlegend: false,
  margin: { t: 20, b: 30, l: 75, r: 75, pad: 0 },
  hovermode: 'x',
  hoverdistance: 50000, // Added as maximum distance to trigger all mouse events properly.
  xaxis: {
    zeroline: false,
    ticklen: 8,
    showgrid: false,
    autorange: false,
    linecolor: color('rthChartBorder'),
    linewidth: 1,
    nticks: 12,
    mirror: true,
    showline: true,
    tickcolor: color('rthChartBorder'),
    tickformatstops: [{
      dtickrange: [null, 1000],
      value: '%-I:%M:%S.%L %p'
    },
    {
      dtickrange: [1000, 60000],
      value: '%-I:%M:%S %p'
    },
    {
      dtickrange: [60000, 3600000],
      value: '%-I:%M %p'
    },
    {
      dtickrange: [3600000, 86400000],
      value: '%-I %p'
    }],
    type: 'date'
  },
  'xaxis.autorange': false,
  'yaxis.autorange': false,
  yaxis: {
    title: 'Temperature °F | Relative Humidity % | µg/m³',
    anchor: 'x',
    fixedrange: true,
    showline: true,
    linecolor: color('rthChartBorder'),
    gridcolor: color('rthChartBorder'),
    zeroline: false,
    mirror: true
  },
  yaxis2: {
    title: 'CFM | PPM | PPB',
    gridcolor: color('rthChartBorder'),
    overlaying: 'y',
    fixedrange: true,
    showline: false,
    zeroline: false,
    side: 'right'
  }
}

const parseTimeZone = (timestamp, timeZone, addMilliseconds = 0) => {
  const t = moment.unix(timestamp / 1000).tz(timeZone)

  return t.add(addMilliseconds, 'milliseconds').format('YYYY-MM-DDTHH:mm:ss.SSS')
}

export const updateLayoutOnResetZoom = (isZoomed, updatedLayout, initialLayout) => {
  const clonedInitialLayout = JSON.parse(JSON.stringify(initialLayout))

  if (isZoomed && updatedLayout) {
    clonedInitialLayout.xaxis.range = [...updatedLayout.xaxis.range]
    return clonedInitialLayout
  } else {
    return clonedInitialLayout
  }
}

export const buildAwairLayout = (fromTime, toTime, traces) => {
  const range = [
    moment(fromTime).format('YYYY-MM-DDTHH:mm:ss.SSS'),
    moment(toTime).format('YYYY-MM-DDTHH:mm:ss.SSS')
  ]

  const layout = JSON.parse(JSON.stringify(baseLayout)) // deepclone layout object

  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis, LEFT_YAXIS)
  setYaxisRangeBasedOnTracesVisibility(traces, layout.yaxis2, RIGHT_YAXIS)
  return { ...layout, xaxis: { ...layout.xaxis, range } }
}

export const buildTraces = (awairEvents, thermostatEvents, displayIndoorUnit, timeZone) => {
  const x: Array<string> = []
  const temperatureData: Array<number | null> = []
  const humidityData: Array<number> = []
  const co2Data: Array<number> = []
  const vocData: Array<number> = []
  const pm25Data: Array<number> = []

  _.each(awairEvents, event => {
    x.push(parseTimeZone(event.occurredAt, timeZone))
    const temp = event.temperature ? ((event.temperature * (9 / 5)) + 32) : null
    temperatureData.push(temp)
    humidityData.push(event.humidity)
    co2Data.push(event.co2)
    vocData.push(event.voc)
    pm25Data.push(event.pm25)
  })

  const actualAirflowOccurredAt: Array<string> = []
  const actualAirflowValue: Array<number> = []

  _.each(thermostatEvents?.actualAirflow, (occurrence) => {
    actualAirflowOccurredAt.push(parseTimeZone(occurrence.occurredAt, timeZone))
    actualAirflowValue.push(occurrence.airflow)
  })

  const traces = [
    createPlotlyTrace(x, temperatureData, LEFT_YAXIS, awairPlot.temperature, 'temp'),
    createPlotlyTrace(x, humidityData, LEFT_YAXIS, awairPlot.humidity, 'humidity'),
    createPlotlyTrace(x, co2Data, RIGHT_YAXIS, awairPlot.co2, 'co2'),
    createPlotlyTrace(x, vocData, RIGHT_YAXIS, awairPlot.voc, 'voc'),
    createPlotlyTrace(x, pm25Data, LEFT_YAXIS, awairPlot.pm25, 'pm25')
  ]
  const airFlowTrace = displayIndoorUnit && createPlotlyTrace(actualAirflowOccurredAt, actualAirflowValue, RIGHT_YAXIS, awairPlot.actualAirflow, 'actualAirflow')
  return displayIndoorUnit && thermostatEvents?.actualAirflow.length > 0 ? [...traces, airFlowTrace] : traces
}

export const updateTracesVisibility = (traces, selectedLegendItems) => {
  return _.map(traces, (trace) => {
    // using width to control visibility because when we mouse hover we can still access those points
    return { ...trace, line: { ...trace.line, width: selectedLegendItems[trace.name] ? 2 : 0 } }
  })
}

const createPlotlyTrace = (x: Array<string>, y: Array<number | null>, yaxis: string, line: Line, name: string): Partial<Plotly.PlotData> => {
  return {
    x,
    y,
    name,
    yaxis,
    line,
    xaxis: 'x',
    visible: true,
    cliponaxis: false,
    type: 'scatter',
    mode: 'lines',
    width: 0,
    marker: {},
    hoverinfo: 'none',
    connectgaps: true
  }
}
